import i18n from '@/i18n/config'

/**
 * The `ERROR_*` codes this application has something better to say about than
 * "the action couldn't be completed".
 *
 * A map to translation key stems rather than to strings, so the pair is resolved
 * at display time in the active language and a missing key is a compile error
 * rather than a `[missing "en..."]` rendered to somebody who is already looking at
 * a failure. `<stem>Title` and `<stem>Description` are the two halves.
 *
 * Anything absent falls through to the generic message. That is the common case
 * and it is fine: the backend has hundreds of codes and only these read better
 * spelled out.
 */
const ERROR_MESSAGE_STEMS = {
  ERROR_USER_NOT_IN_GROUP: 'userNotInWorkspace',
  ERROR_USER_INVALID_GROUP_PERMISSIONS: 'invalidWorkspacePermissions',
  ERROR_TABLE_DOES_NOT_EXIST: 'tableDoesNotExist',
  ERROR_ROW_DOES_NOT_EXIST: 'rowDoesNotExist',
  ERROR_CANNOT_CREATE_FIELD_TYPE: 'cannotCreateFieldType',
  ERROR_NOTIFICATION_DOES_NOT_EXIST: 'notificationDoesNotExist',
  ERROR_FILE_SIZE_TOO_LARGE: 'fileSizeTooLarge',
  ERROR_INVALID_FILE: 'invalidFile',
  ERROR_FILE_URL_COULD_NOT_BE_REACHED: 'fileUrlCouldNotBeReached',
  ERROR_INVALID_FILE_URL: 'invalidFileUrl',
  ERROR_MAX_FIELD_COUNT_EXCEEDED: 'maxFieldCountExceeded',
  ERROR_CANNOT_RESTORE_PARENT_BEFORE_CHILD: 'cannotRestoreParentBeforeChild',
  ERROR_CANT_RESTORE_AS_RELATED_TABLE_TRASHED: 'cannotRestoreAsRelatedTableTrashed',
  ERROR_GROUP_USER_IS_LAST_ADMIN: 'workspaceUserIsLastAdmin',
  ERROR_CANNOT_DELETE_YOURSELF_FROM_GROUP: 'cannotDeleteYourselfFromWorkspace',
  ERROR_CANNOT_SUSPEND_YOURSELF: 'cannotSuspendYourself',
  ERROR_WORKSPACE_NOT_TENANT_WORKSPACE: 'workspaceNotTenantWorkspace',
  ERROR_CANNOT_CHANGE_TENANT_WORKSPACE: 'cannotChangeTenantWorkspace',
  ERROR_MAX_JOB_COUNT_EXCEEDED: 'errorMaxJobCountExceeded',
  ERROR_FAILED_TO_LOCK_FIELD_DUE_TO_CONFLICT: 'failedToLockFieldDueToConflict',
  ERROR_FAILED_TO_LOCK_TABLE_DUE_TO_CONFLICT: 'failedToLockTableDueToConflict',
  ERROR_UNDO_REDO_LOCK_CONFLICT: 'failedToUndoRedoDueToConflict',
  ERROR_MAXIMUM_SNAPSHOTS_REACHED: 'maximumSnapshotsReached',
  ERROR_SUBSCRIPTION_LIMIT_EXCEEDED: 'subscriptionLimitExceeded',
  ERROR_SNAPSHOT_IS_BEING_CREATED: 'snapshotBeingCreated',
  ERROR_SNAPSHOT_IS_BEING_RESTORED: 'snapshotBeingRestored',
  ERROR_SNAPSHOT_IS_BEING_DELETED: 'snapshotBeingDeleted',
  ERROR_SNAPSHOT_NAME_NOT_UNIQUE: 'snapshotNameNotUnique',
  ERROR_SNAPSHOT_OPERATION_LIMIT_EXCEEDED: 'snapshotOperationLimitExceeded',
  ERROR_MAX_LOCKS_PER_TRANSACTION_EXCEEDED: 'maxLocksPerTransactionExceeded',
  ERROR_LAST_ADMIN_OF_GROUP: 'lastAdmin',
  ERROR_FIELD_IS_ALREADY_PRIMARY: 'fieldIsAlreadyPrimary',
  ERROR_INCOMPATIBLE_PRIMARY_FIELD_TYPE: 'incompatiblePrimaryFieldType',
  ERROR_CANNOT_CREATE_ROWS_IN_TABLE: 'cannotCreateRowsInTable',
  ERROR_DATABASE_DEADLOCK: 'databaseDeadlock',
  ERROR_UNIQUE_PRIMARY_PROPERTY_NOT_FOUND: 'databaseUniquePrimaryPropertyNotFound',
  ERROR_FIELD_DATA_CONSTRAINT: 'fieldDataConstraint',
  ERROR_FIELD_CONSTRAINT: 'fieldConstraint',
} as const

export type MappedErrorCode = keyof typeof ERROR_MESSAGE_STEMS

export interface ErrorMessage {
  title: string
  description: string
}

function message(stem: string): ErrorMessage {
  return {
    // Cast because the stems are assembled at runtime; the pairs themselves are
    // covered by `errorMessageStemsHaveBothHalves` in the tests, which walks the
    // table against the catalogue.
    title: i18n.t(`apiError.${stem}Title` as 'apiError.notCompletedTitle'),
    description: i18n.t(
      `apiError.${stem}Description` as 'apiError.notCompletedDescription',
    ),
  }
}

export function genericErrorMessage(): ErrorMessage {
  return message('notCompleted')
}

export function networkErrorMessage(): ErrorMessage {
  return message('networkError')
}

export function tooManyRequestsMessage(): ErrorMessage {
  return message('tooManyRequests')
}

export function notFoundMessage(name: string): ErrorMessage {
  return {
    title: i18n.t('apiError.notFoundTitle', { name }),
    description: i18n.t('apiError.notFoundDescription', { name }),
  }
}

/** The message for a code, or null when nothing better than the generic exists. */
export function messageForCode(code: string | null): ErrorMessage | null {
  if (code === null) return null
  const stem = ERROR_MESSAGE_STEMS[code as MappedErrorCode] as string | undefined
  return stem === undefined ? null : message(stem)
}

/** Test seam: lets a spec assert every stem resolves to a real pair of strings. */
export const __errorMessageStems = ERROR_MESSAGE_STEMS
